/**
 * Pendo Analytics Integration
 *
 * Initializes Pendo.io for user analytics and product insights.
 * Enabled for all environments (local, dev, staging, production).
 * Visitor, account, and event metadata include an `environment` field for filtering in Pendo.
 */

import AppConfig from '../AppConfig';

declare global {
  interface Window {
    pendo?: {
      initialize: (config: PendoConfig) => void;
      isReady: () => boolean;
      identify: (config: PendoConfig) => void;
      updateOptions: (config: Partial<PendoConfig>) => void;
      pageLoad: () => void;
      track: (eventName: string, metadata?: Record<string, any>) => void;
      trackAgent: (eventName: string, metadata?: Record<string, any>) => void;
    };
  }
}

interface PendoVisitor {
  id: string;
  email?: string;
  full_name?: string;
  role?: string;
  userType?: string;
  /** Environment for filtering in Pendo: 'local' | 'dev' | 'staging' | 'production' */
  environment?: string;
}

interface PendoAccount {
  id: string;
  name?: string;
  appAccountType?: string;
  status?: string;
  /** Environment for filtering in Pendo: 'local' | 'dev' | 'staging' | 'production' */
  environment?: string;
}

interface PendoConfig {
  visitor: PendoVisitor;
  account: PendoAccount;
}

/** Human-readable Pendo event names (used in dashboards and code) */
export const PENDO_EVENTS = {
  COURSE_ASSET_DOWNLOAD: 'Course Asset Download',
  COURSE_ASSET_PREVIEW: 'Course Asset Preview',
  COURSE_ASSET_SEARCH: 'Course Asset Search',

  /** CEE-838 legacy sample preview launch (CEE-1025) — separate from entitled library events */
  CE_SAMPLE_BANNER_CLICKED: 'CE Sample Banner Clicked',
  /** Introducing CE hub — sample content list page (not asset preview) */
  CE_SAMPLE_CONTENT_LIST_VIEWED: 'CE Sample Content List Viewed',
  /** User clicks Open on a sample card from the content list → sample course detail */
  CE_SAMPLE_CONTENT_OPENED: 'CE Sample Content Opened',
  CE_SAMPLE_ASSET_PREVIEWED: 'CE Sample Asset Previewed',
  /** Explore packages header CTA — only tracked when button is visible (URL configured) */
  CE_SAMPLE_EXPLORE_PACKAGES_CLICKED: 'CE Sample Explore Packages Clicked',

  /** Content Authoring — CEE-1211 (23 events; productArea via trackAuthoringPendoEvent) */
  AUTHORING_TEMPLATE_CREATED: 'Authoring Template Created',
  AUTHORING_TEMPLATE_MARKED_READY: 'Authoring Template Marked Ready',
  AUTHORING_TEMPLATE_ARCHIVED: 'Authoring Template Archived',
  AUTHORING_TEMPLATE_DUPLICATED: 'Authoring Template Duplicated',
  AUTHORING_COURSE_CREATED: 'Authoring Course Created',
  AUTHORING_COURSE_MARKED_READY: 'Authoring Course Marked Ready',
  AUTHORING_COURSE_ARCHIVED: 'Authoring Course Archived',
  AUTHORING_COURSE_DUPLICATED: 'Authoring Course Duplicated',
  AUTHORING_TRANSLATION_CREATED: 'Authoring Translation Created',
  AUTHORING_TRANSLATION_APPROVED: 'Authoring Translation Approved',
  AUTHORING_TRANSLATION_ARCHIVED: 'Authoring Translation Archived',
  AUTHORING_COURSE_SUBMITTED_FOR_REVIEW: 'Authoring Course Submitted for Review',
  AUTHORING_SCORM_EXPORT_COMPLETED: 'Authoring SCORM Export Completed',
  AUTHORING_ADD_TRANSLATIONS_CLICKED: 'Authoring Add Translations Clicked',
  AUTHORING_TRANSLATION_APPROVE_CLICKED: 'Authoring Translation Approve Clicked',
  AUTHORING_NEW_TEMPLATE_CLICKED: 'Authoring New Template Clicked',
  AUTHORING_NEW_COURSE_CLICKED: 'Authoring New Course Clicked',
  AUTHORING_SCORM_EXPORT_CLICKED: 'Authoring SCORM Export Clicked',
  AUTHORING_COURSE_SUBMIT_FOR_REVIEW_CLICKED: 'Authoring Course Submit for Review Clicked',
  AUTHORING_TEMPLATE_MARK_READY: 'Authoring Template Mark Ready',
  AUTHORING_COURSE_MARK_READY_CLICKED: 'Authoring Course Mark Ready Clicked',
  AUTHORING_SLIDE_IMAGE_CHANGED: 'Authoring Slide Image Changed',
  AUTHORING_BRAND_COLORS_APPLIED: 'Authoring Brand Colors Applied',
} as const;

export type CeSamplePreviewSection = 'code_of_conduct' | 'standalone' | 'additional_assets';

/** Map catalog category to Pendo section property for sample preview events. */
export function toCeSamplePreviewSection(category?: string): CeSamplePreviewSection | undefined {
  if (category === 'code-of-conduct') return 'code_of_conduct';
  if (category === 'extended-modules') return 'standalone';
  if (category === 'additional-assets') return 'additional_assets';
  return undefined;
}

export type CeSamplePreviewAnalytics = {
  section: CeSamplePreviewSection;
  libraryId: string;
};

/** Map file extension to Pendo previewType (pdf, video, ppt, etc.) */
export function getCourseAssetPreviewType(fileNameOrKey: string): string {
  const ext = fileNameOrKey.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'pdf') return 'pdf';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return 'video';
  if (ext === 'zip') return 'scorm';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (['doc', 'docx'].includes(ext)) return 'doc';
  return ext || 'unknown';
}

export function trackCourseAssetPreview({
  courseId,
  fileName,
  fileKey,
}: {
  courseId: string;
  fileName: string;
  fileKey?: string;
}): void {
  const typeSource = fileKey && getCourseAssetPreviewType(fileKey) !== 'unknown' ? fileKey : fileName;
  trackPendoEvent(PENDO_EVENTS.COURSE_ASSET_PREVIEW, {
    courseId,
    previewType: getCourseAssetPreviewType(typeSource),
    fileName,
  });
}

export function trackCeSampleAssetPreviewed({
  libraryId,
  fileName,
  fileKey,
  section,
}: {
  libraryId: string;
  fileName: string;
  fileKey?: string;
  section: CeSamplePreviewSection;
}): void {
  const typeSource = fileKey && getCourseAssetPreviewType(fileKey) !== 'unknown' ? fileKey : fileName;
  trackPendoEvent(PENDO_EVENTS.CE_SAMPLE_ASSET_PREVIEWED, {
    libraryId,
    previewType: getCourseAssetPreviewType(typeSource),
    fileName,
    section,
  });
}

/** Environment string for Pendo filtering: 'local' | 'dev' | 'staging' | 'production' */
function getPendoEnvironment(): string {
  if (AppConfig.isLocal) return 'local';
  if (AppConfig.isProduction) return 'production';
  if (AppConfig.isPreprod || AppConfig.isStaging) return 'staging';
  if (AppConfig.isDev) return 'dev';
  return 'local';
}

/**
 * Initialize Pendo with user and account data
 * @param userId - Unique user identifier
 * @param userEmail - User's email address
 * @param userName - User's full name
 * @param userRole - User's primary role
 * @param userType - User type (super_admin, account_admin, content_manager, standard)
 * @param accountId - Organization/Account ID
 * @param accountName - Organization/Account name
 * @param appAccountType - Account type for UI customization
 * @param accountStatus - Account status
 */
export function initializePendo(
  userId: string,
  userEmail?: string,
  userName?: string,
  userRole?: string,
  userType?: string,
  accountId?: string,
  accountName?: string,
  appAccountType?: string,
  accountStatus?: string,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const environment = getPendoEnvironment();

  // Prefer runtime config (config.json), then build-time Vite env, then default key
  // TODO: Remove DEFAULT_PENDO_API_KEY after testing; require key from config or env only
  const DEFAULT_PENDO_API_KEY = '85c336b3-4d18-4b76-9bd2-cf3fdd4f60d3';
  const pendoApiKey =
    (typeof window !== 'undefined' && window.__CONFIG__?.pendoApiKey) ||
    import.meta.env.VITE_PENDO_API_KEY ||
    DEFAULT_PENDO_API_KEY ||
    '';

  if (!pendoApiKey) {
    console.info('[Pendo] API key not configured, skipping initialization');
    return;
  }

  console.debug('[Pendo] initializePendo called with:', {
    userId,
    userEmail,
    userName,
    userRole,
    userType,
    accountId,
  });

  // Load Pendo script if not already loaded
  if (!window.pendo) {
    loadPendoScript(pendoApiKey);
  }

  // Wait for Pendo to be ready, then initialize
  waitForPendo(() => {
    try {
      const config: PendoConfig = {
        visitor: {
          id: userId,
          email: userEmail,
          full_name: userName,
          role: userRole,
          userType: userType,
          environment,
        },
        account: {
          id: accountId || 'unknown',
          name: accountName,
          appAccountType: appAccountType,
          status: accountStatus,
          environment,
        },
      };

      console.info('[Pendo] Initializing with config:', {
        visitorId: config.visitor.id,
        accountId: config.account.id,
      });

      console.debug('[Pendo] Full config:', config);

      window.pendo!.initialize(config);
    } catch (error) {
      console.error('[Pendo] Failed to initialize:', error);
    }
  });
}

/**
 * Load Pendo script dynamically
 * Based on official Pendo template
 */
function loadPendoScript(publicAppId: string): void {
  // eslint-disable-next-line prefer-rest-params
  (function (p: any, e: any, n: any, d: any) {
    let v: any, w: any, x: any, y: any, z: any;
    const o: any = (p[d] = p[d] || {});
    o._q = o._q || [];

    v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track', 'trackAgent'];
    for (w = 0, x = v.length; w < x; ++w) {
      (function (m: any) {
        o[m] =
          o[m] ||
          function () {
            // eslint-disable-next-line prefer-rest-params
            o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
          };
      })(v[w]);
    }

    y = e.createElement(n);
    y.async = true;
    y.src = 'https://cdn.pendo.io/agent/static/' + publicAppId + '/pendo.js';
    z = e.getElementsByTagName(n)[0];
    z.parentNode.insertBefore(y, z);
  })(window, document, 'script', 'pendo');
}

/**
 * Wait for Pendo to be available
 */
function waitForPendo(callback: () => void, maxAttempts = 50): void {
  if (typeof window === 'undefined') {
    return;
  }

  let attempts = 0;
  const interval = setInterval(() => {
    if (typeof window === 'undefined') {
      clearInterval(interval);
      return;
    }

    attempts++;
    if (window.pendo && typeof window.pendo.initialize === 'function') {
      clearInterval(interval);
      callback();
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
      console.warn('[Pendo] Timed out waiting for Pendo to load');
    }
  }, 100);
}

/**
 * Check if Pendo is ready
 */
export function isPendoReady(): boolean {
  return window.pendo?.isReady?.() ?? false;
}

/**
 * Track a custom event in Pendo (e.g. user actions like downloads).
 * No-op if Pendo is not loaded or not ready.
 * Environment is always included in event metadata for filtering in Pendo.
 * @param eventName - Event name (use PENDO_EVENTS for readable names, e.g. PENDO_EVENTS.COURSE_ASSET_DOWNLOAD)
 * @param metadata - Optional key-value data to attach to the event (environment is merged in automatically)
 */
export function trackPendoEvent(eventName: string, metadata?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !window.pendo?.track) return;
  try {
    const eventMetadata = {
      environment: getPendoEnvironment(),
      ...(metadata ?? {}),
    };
    window.pendo.track(eventName, eventMetadata);
  } catch (error) {
    console.warn('[Pendo] track failed:', error);
  }
}
